import { Link } from '@inertiajs/react'
import { Trophy, Star, Medal } from 'lucide-react'
import type { LeaderboardUser } from '@/types'

interface LeaderboardSidebarProps {
  users: LeaderboardUser[]
  currentUserRank: number
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getRankIcon(rank: number) {
  switch (rank) {
    case 1:
      return <Trophy className="w-5 h-5 text-yellow-500" />
    case 2:
      return <Medal className="w-5 h-5 text-gray-400" />
    case 3:
      return <Medal className="w-5 h-5 text-amber-600" />
    default:
      return <span className="w-5 text-center text-gray-500 font-medium">{rank}</span>
  }
}

export default function LeaderboardSidebar({ users, currentUserRank }: LeaderboardSidebarProps) {
  if (users.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-tessan-primary" />
          Classement
        </h3>
        <p className="text-gray-500 text-sm text-center py-4">
          Aucun membre pour le moment
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-tessan-primary" />
        Classement
      </h3>

      <div className="space-y-2">
        {users.map((user) => (
          <Link
            key={user.id}
            href={`/users/${user.id}`}
            className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
              user.is_current_user
                ? 'bg-tessan-cream border border-tessan-primary/20'
                : 'hover:bg-gray-50'
            }`}
          >
            {/* Rank */}
            <div className="flex-shrink-0 w-6 flex justify-center">
              {getRankIcon(user.rank)}
            </div>

            {/* Avatar */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-tessan-primary text-white flex items-center justify-center text-xs font-medium">
              {getInitials(user.name)}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className={`font-medium truncate ${user.is_current_user ? 'text-tessan-primary' : 'text-gray-900'}`}>
                  {user.name}
                </span>
                {user.is_current_user && (
                  <span className="text-xs text-tessan-primary">(vous)</span>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Star className="w-3 h-3" />
                <span>Niveau {user.level}</span>
              </div>
            </div>

            {/* Points */}
            <div className="flex-shrink-0 text-right">
              <span className="font-semibold text-tessan-primary">{user.total_points}</span>
              <span className="text-xs text-gray-500 ml-1">pts</span>
            </div>
          </Link>
        ))}
      </div>

      {currentUserRank > 10 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Votre position : <span className="font-semibold text-tessan-primary">{currentUserRank}e</span>
          </p>
        </div>
      )}
    </div>
  )
}
